/**
 * **Measure on touch and pen** (developer, 2026-09-28): the stage's mouse gestures ride `mousedown` /
 * `mousemove` / `mouseup`, which a finger only synthesises for a tap and never for a drag. While
 * Measure is armed, this feeds the same `MeasurePointer` from pointer events for every non-mouse
 * pointer, so a press-drag shows the ruler until the lift and a tap, tap reads like a click, click.
 * A mouse is left to the mouse path untouched.
 *
 * The press is `preventDefault`ed, which stops the browser synthesising `mousedown`/`mouseup` for the
 * tap (the mouse path would otherwise press twice), and the pointer is captured on the host so a
 * drag that leaves the board still ends on its lift. Only the primary pointer measures; the Pixi
 * canvas is `touch-action: none`, so the board has no native touch pan or pinch for this to take.
 * Unarmed, nothing here runs.
 */
import { useEffect, useRef } from "react";
import { isMeasureDrag, type MeasureEnd } from "../combatApp/measureTool";
import type { MeasurePointer } from "../combatApp/useMeasureTool";

export function useMeasureTouch(args: {
  host: HTMLElement | null;
  armed: boolean;
  /** The end under a pointer event, or `null` off the board. */
  endAt: (e: PointerEvent) => MeasureEnd | null;
  onPointer: MeasurePointer | undefined;
}): void {
  const { host, armed } = args;
  const latest = useRef(args);
  latest.current = args;

  useEffect(() => {
    if (!host || !armed) return;
    let active: { id: number; x: number; y: number } | null = null;
    const onDown = (e: PointerEvent) => {
      if (e.pointerType === "mouse" || !e.isPrimary || e.button !== 0) return;
      const end = latest.current.endAt(e);
      if (!end) return;
      e.preventDefault();
      active = { id: e.pointerId, x: e.clientX, y: e.clientY };
      if (host.hasPointerCapture && !host.hasPointerCapture(e.pointerId)) host.setPointerCapture(e.pointerId);
      latest.current.onPointer?.({ phase: "move", end });
      latest.current.onPointer?.({ phase: "press", end, touch: true });
    };
    const onMove = (e: PointerEvent) => {
      if (active?.id !== e.pointerId) return;
      const end = latest.current.endAt(e);
      if (end) latest.current.onPointer?.({ phase: "move", end });
    };
    const onUp = (e: PointerEvent) => {
      if (active?.id !== e.pointerId) return;
      const dragged = isMeasureDrag(active, { x: e.clientX, y: e.clientY });
      active = null;
      latest.current.onPointer?.({ phase: "release", dragged });
    };
    // A cancelled pointer (the OS took the gesture) ends the ruler rather than leaving it live.
    const onCancel = (e: PointerEvent) => {
      if (active?.id !== e.pointerId) return;
      active = null;
      latest.current.onPointer?.({ phase: "release", dragged: true });
    };
    host.addEventListener("pointerdown", onDown);
    host.addEventListener("pointermove", onMove);
    host.addEventListener("pointerup", onUp);
    host.addEventListener("pointercancel", onCancel);
    return () => {
      host.removeEventListener("pointerdown", onDown);
      host.removeEventListener("pointermove", onMove);
      host.removeEventListener("pointerup", onUp);
      host.removeEventListener("pointercancel", onCancel);
    };
  }, [host, armed]);
}
